import React, { ChangeEvent, useState } from 'react';
import { makeStyles } from '@mui/styles';
import { Select, Input, Typography, Button, MenuItem, Theme, SelectChangeEvent, FormControl } from '@mui/material';
import { countries, numbers } from '../../constants';
import { Call } from '@twilio/voice-sdk';
import { apiGetPhoneExtension } from '../../utils/api';
import { useAppState } from '../../state';

const useStyles = makeStyles((theme: Theme) => {
  return {
    manualContainer: {
      textAlign: 'center',
    },
    phoneInputContainer: {
      textAlign: 'center',
    },
    countryCode: {
      lineHeight: '0.4875em',
      border: 'solid 1px rgba(0,0,0,0.42)',
      borderBottom: '0',
    },
    phoneInput: {
      height: 32,
      border: 'solid 1px rgba(0,0,0,0.42)',
      borderBottom: '0',
      borderLeft: '0',
      padding: '0px 8px',
    },
    callBtn: {
      width: '250px',
      textTransform: 'uppercase',
      marginTop: '16px',
      left: '50%',
      transform: 'translateX(-50%)',
      padding: '8px',
    },
    keypadContainer: {
      display: 'grid',
      gridTemplateColumns: 'repeat(3, 1fr)',
      gridColumnGap: '15px',
      gridRowGap: '25px',
      maxWidth: '180px',
      margin: '16px auto 0',
    },
    numberContainer: {
      height: '50px',
      width: '50px',
      borderRadius: '50%',
      border: '1px solid #ccc',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      position: 'relative',
      cursor: 'pointer',
      transition: 'background .3s, border-color .3s',
      '&:hover': {
        background: '#e6e6e6',
        borderColor: '#adadad',
      },
    },
    numbers: {},
    alphabets: {
      position: 'absolute',
      top: 'calc(100%)',
    },
  };
});

interface VoiceKeypadProps {
  handleCall: (toNumber: string) => void;
  activeCall?: Call | null;
}

const VoiceKeypad = ({ handleCall, activeCall }: VoiceKeypadProps) => {
  const classes = useStyles();
  const [phoneNumber, setPhoneNumber] = useState<string>('');
  const [countryCode, setCountryCode] = useState<string>(countries[0].cc);
  const [connectedNumber, setConnectedNumber] = useState<string>(null!);
  const { domain } = useAppState();

  const handleNumberChange = (event: ChangeEvent<HTMLInputElement>) => {
    const regex = /^[1-9][0-9]*$/;
    const val = event.target.value;
    if (!val || regex.test(val)) {
      setPhoneNumber(val);
    }
  };

  const handleCountryCodeChange = (e: SelectChangeEvent<string>) => {
    setCountryCode(e.target.value);
  };

  const handleNumberClick = (numberObj: { num: string; alphabets: string }) => {
    if (activeCall) {
      activeCall && activeCall.sendDigits(numberObj.num);
      setConnectedNumber(connectedNumber ? connectedNumber + numberObj.num : numberObj.num);
    } else {
      setPhoneNumber(phoneNumber + numberObj.num);
    }
  };

  // combines countrycode and phonenumber and invokes handleCall
  const handleCallClick = () => {
    // initialize number to full number
    let toNumber = countryCode + phoneNumber;
    // check for empty input or too many numbers and prompt no people with extension
    if (String(phoneNumber).length === 0 || String(phoneNumber).length > 10) {
      window.alert('Sorry, no people with that extension');
      console.log('Sorry, no people with that extension');
    }
    // if number is between 0 and 10, extract PBX number from apiGetPhoneExtension call
    else if (String(phoneNumber).length > 0 && String(phoneNumber).length < 10) {
      apiGetPhoneExtension(phoneNumber, domain)
        .then(res => res.json())
        .then(data => {
          // if extension doesn't match pbx then don't call
          if (data[0]?.PBX) {
            // set pbx field to retrieved value
            const PBX = data[0]?.PBX;
            // add countrycode before the phone number
            toNumber = countryCode + PBX;
            handleCall(toNumber);
          } else {
            window.alert('Sorry, a data error has occurred');
          }
        });
    } else {
      handleCall(toNumber);
    }
  };

  return (
    <div className={classes.manualContainer}>
      {!activeCall && (
        <div className={classes.phoneInputContainer}>
          <FormControl size="small">
            <Select
              labelId="country-code-label"
              id="country-code-select"
              value={countryCode}
              onChange={handleCountryCodeChange}
              autoWidth
              sx={{
                borderRadius: 0,
                height: 32,
                '& .MuiSelect-select': {
                  py: 0,
                  display: 'flex',
                  alignItems: 'center',
                },
              }}
            >
              {countries.map(country => (
                <MenuItem value={country.cc} key={country.cc}>
                  +{country.cc}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Input
            placeholder="555-666-7777"
            inputProps={{ 'aria-label': 'number' }}
            value={phoneNumber}
            onChange={handleNumberChange}
            className={classes.phoneInput}
          />
        </div>
      )}
      {activeCall && <Input disabled value={connectedNumber} />}
      <div className={classes.keypadContainer}>
        {numbers.map(number => (
          <div
            className={classes.numberContainer}
            onClick={() => handleNumberClick(number)}
            key={number.num + number.alphabets}
          >
            <Typography className={classes.numbers} variant="body1">
              {number.num}
            </Typography>
            <Typography className={classes.alphabets} variant="body1">
              {number.alphabets}
            </Typography>
          </div>
        ))}
      </div>
      {!activeCall && (
        <Button className={classes.callBtn} variant="contained" color="primary" onClick={handleCallClick}>
          Call
        </Button>
      )}
    </div>
  );
};

VoiceKeypad.defaultProps = {
  isConnected: false,
};

export default VoiceKeypad;
